/**
 * A skill's run history: each run, how it started (by hand, on a schedule,
 * from an event), where it stands, what the assistant said it did, the
 * attorney's thumbs on it, and a link to the case thread that is its
 * transcript. A completed run whose report leaves out something the skill's
 * Result promises is flagged "May be incomplete" and asks for a rating. A
 * thumbs-down (or any run) can be sent to the builder with "Improve from
 * this run".
 */
import {
  AlertTriangle,
  CalendarClock,
  ExternalLink,
  FlaskConical,
  Hand,
  Play,
  ThumbsDown,
  ThumbsUp,
  Wand2,
  Zap
} from "lucide-react";
import { useState, type FC } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Tag } from "@/components/ui/tag";
import { Textarea } from "@/components/ui/textarea";
import { caseWorkspacePath } from "@/features/caseIde/routes";
import { THREAD_PARAM } from "@/features/caseIde/chat/useThreadDeepLink";
import { cn } from "@/lib/utils";

import { automationsPath } from "@/features/automations/routes";
import type {
  FirmSkillRun,
  FirmSkillRunTrigger
} from "@/features/firmKnowledge/types";
import { SkillRunStatusBadge } from "./SkillBadges";
import {
  RUN_OUTCOME_LABEL,
  fullDate,
  outcomeNote,
  shortDate
} from "./skillModel";

type Props = {
  runs: FirmSkillRun[];
  loading: boolean;
  error: string | null;
  /** Records a rating; absent when the viewer may not rate. */
  onRate?: (
    runId: string,
    rating: "up" | "down" | "",
    note?: string
  ) => Promise<unknown>;
};

/** The case thread the run lives in. */
export const runThreadPath = (run: FirmSkillRun): string =>
  `${caseWorkspacePath({
    individualId: run.individual_id
  })}?${THREAD_PARAM}=${encodeURIComponent(run.thread_id)}`;

const TRIGGER: Record<
  FirmSkillRunTrigger,
  { label: string; Icon: typeof Hand }
> = {
  manual: { label: "By hand", Icon: Hand },
  schedule: { label: "On schedule", Icon: CalendarClock },
  event: { label: "On an event", Icon: Zap },
  test: { label: "Test", Icon: FlaskConical }
};

const RunTrigger: FC<{ trigger: FirmSkillRunTrigger; by?: string | null }> = ({
  trigger,
  by
}) => {
  const meta = TRIGGER[trigger] ?? TRIGGER.manual;
  const label = trigger === "manual" && by ? `Started by ${by}` : meta.label;
  return (
    <span
      className="inline-flex items-center gap-1 text-sm text-fg-3"
      data-testid="run-trigger"
      data-trigger={trigger}
    >
      <meta.Icon className="size-3" /> {label}
    </span>
  );
};

/** Thumbs up/down with an optional note on a thumbs-down. */
const RunFeedbackControl: FC<{
  run: FirmSkillRun;
  onRate: NonNullable<Props["onRate"]>;
}> = ({ run, onRate }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(run.feedback?.note ?? "");
  const [busy, setBusy] = useState(false);
  const rating = run.feedback?.rating ?? null;

  const rate = async (next: "up" | "down" | "", text?: string) => {
    setBusy(true);
    try {
      await onRate(run.id, next, text);
      setOpen(false);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not record the rating."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1" data-testid="run-feedback">
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        aria-label="Good run"
        aria-pressed={rating === "up"}
        disabled={busy}
        className={cn(rating === "up" && "bg-success-soft text-success")}
        onClick={() => void rate(rating === "up" ? "" : "up")}
        data-testid="run-thumbs-up"
      >
        <ThumbsUp className="size-3.5" />
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            aria-label="Bad run"
            aria-pressed={rating === "down"}
            disabled={busy}
            className={cn(rating === "down" && "bg-danger-soft text-danger")}
            data-testid="run-thumbs-down"
          >
            <ThumbsDown className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 space-y-2">
          <p className="text-sm font-medium">What went wrong?</p>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="A sentence or two the builder can learn from."
            data-testid="run-feedback-note"
          />
          <div className="flex justify-end gap-2">
            {rating === "down" ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void rate("")}
              >
                Clear
              </Button>
            ) : null}
            <Button
              variant="primary"
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => void rate("down", note)}
              data-testid="run-feedback-save"
            >
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const SkillRunsList: FC<Props> = ({ runs, loading, error, onRate }) => {
  if (loading && runs.length === 0) {
    return (
      <div className="flex items-center gap-2 px-6 py-6 text-base text-fg-3">
        <Spinner className="size-3.5" /> Loading runs…
      </div>
    );
  }
  if (error) {
    return <p className="px-6 py-6 text-base text-danger">{error}</p>;
  }
  if (runs.length === 0) {
    return (
      <EmptyState
        icon={Play}
        title="No Runs Yet"
        description="Run the automation on a case from here, or from the picker in a case's chat, and each run is listed here with where it stands."
        className="py-10"
        data-testid="skill-runs-empty"
      />
    );
  }
  return (
    <Table
      data-testid="skill-runs"
      className="[&_td:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6"
    >
      <TableHeader>
        <TableRow>
          <TableHead>Status</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Result</TableHead>
          <TableHead className="text-right">Open</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((run) => {
          const incomplete = outcomeNote(run.outcome);
          const rated = run.feedback?.rating;
          return (
            <TableRow
              key={run.id}
              data-testid="skill-run-row"
              data-trigger={run.trigger}
            >
              <TableCell className="align-top">
                <div className="flex flex-wrap items-center gap-1">
                  <SkillRunStatusBadge status={run.status} />
                  {run.trigger === "test" ? (
                    <Tag data-testid="run-test-tag">Test</Tag>
                  ) : null}
                </div>
                {incomplete ? (
                  <div
                    className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-warn"
                    data-testid="run-outcome-partial"
                  >
                    <AlertTriangle className="size-3" /> May Be Incomplete
                  </div>
                ) : null}
                {run.error ? (
                  <div className="mt-1 max-w-64 truncate text-sm text-fg-3">
                    {run.error}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="align-top text-sm text-fg-3">
                <div title={fullDate(run.started_at)}>
                  {shortDate(run.started_at)}
                </div>
                <div className="mt-0.5 flex flex-col gap-0.5 text-sm">
                  <RunTrigger trigger={run.trigger} by={run.started_by_name} />
                  <span>
                    {run.mode === "plan" ? "Plan First" : "Do the Work"} · v
                    {run.skill_version}
                  </span>
                </div>
              </TableCell>
              <TableCell className="align-top text-sm whitespace-normal">
                {run.event?.headline ? (
                  <p
                    className="mb-1 text-sm font-medium"
                    data-testid="run-event"
                  >
                    {run.event.headline}
                  </p>
                ) : null}
                {run.outcome_status ? (
                  <p
                    className="mb-1 text-sm text-fg-3"
                    data-testid="run-outcome-status"
                  >
                    <span className="font-medium text-fg">
                      {RUN_OUTCOME_LABEL[run.outcome_status]}
                    </span>
                    {run.outcome_summary ? ` · ${run.outcome_summary}` : ""}
                  </p>
                ) : null}
                {run.summary && run.summary !== run.outcome_summary ? (
                  <p
                    className="line-clamp-3 text-fg-3"
                    data-testid="run-summary"
                  >
                    {run.summary}
                  </p>
                ) : run.outcome_status ? null : (
                  <span className="text-sm italic text-fg-3">
                    {run.status === "running" ? "Working…" : "—"}
                  </span>
                )}
                {incomplete ? (
                  <p
                    className="mt-1 text-sm text-warn"
                    data-testid="run-outcome-note"
                  >
                    {incomplete}
                  </p>
                ) : null}
                {run.feedback?.note ? (
                  <p className="mt-1 text-sm text-fg-3">
                    Feedback: {run.feedback.note}
                  </p>
                ) : null}
                {onRate || rated ? (
                  <div className="mt-1.5 flex items-center gap-2 text-sm text-fg-3">
                    {incomplete && onRate && !rated ? (
                      <span data-testid="run-rate-prompt">
                        Did it finish the job?
                      </span>
                    ) : null}
                    {onRate ? (
                      <RunFeedbackControl run={run} onRate={onRate} />
                    ) : (
                      <span>Rated {rated === "up" ? "Good" : "Bad"}</span>
                    )}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="w-36 align-top text-right">
                <div className="flex flex-col items-end gap-1">
                  <Link
                    to={runThreadPath(run)}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    data-testid="skill-run-open"
                  >
                    Case Thread <ExternalLink className="size-3" />
                  </Link>
                  <Link
                    to={automationsPath({
                      skillId: run.skill_id,
                      fromRun: run.id
                    })}
                    className="inline-flex items-center gap-1 text-sm text-fg-3 hover:text-fg hover:underline"
                    data-testid="skill-run-improve"
                  >
                    <Wand2 className="size-3" /> Improve From This Run
                  </Link>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
